/**
 * Maps old (Nuxt) marketplace paths to their new Livewire "/v2" equivalent.
 *
 * Used by both the server connect-middleware (server/middleware/force-v2-redirect.js,
 * for hard navigations / external entry) and the client route middleware
 * (middleware/forceV2Middleware.js, for in-SPA navigations) so the mapping lives in
 * exactly one place.
 *
 * Strategy: an explicit allow-list. We only redirect paths we have a confident v2
 * target for; anything else (and any active Nuxt-only flow such as checkout payment
 * returns, signup, invites) is left untouched so it keeps working until it is migrated.
 * A rule with a `null` target is an explicit pass-through that stops a later, more
 * generic rule from matching (e.g. storefront signup must not fall into the generic
 * storefront rule).
 */

export const MARKETPLACE_V2_FEATURE = 'marketplace-v2'

const SUPPORTED_LOCALES = ['nl', 'de', 'fr', 'pl', 'da', 'it', 'es']
const LOCALE_PREFIX_RE = new RegExp(
    '^/(' + SUPPORTED_LOCALES.join('|') + ')(?=/|$)',
    'i'
)

/**
 * Ordered list of [matcher, target] rules. First match wins.
 * `target` of `null` means: do not redirect (explicit pass-through).
 * Within `target`, `$1`/`$2` reference the matcher's capture groups; an unmatched
 * optional group resolves to an empty string.
 */
const RULES = [
    // Home (no trailing slash, so the redirect lands directly on /v2 without a
    // follow-up trailing-slash redirect)
    [/^\/$/, '/v2'],

    // Storefront / brand (specific routes before the generic catch-all)
    [/^\/store\/([a-z0-9-]+)\/listings\/(\d+)$/, '/v2/store/$1/listings/$2'],
    [/^\/store\/([a-z0-9-]+)\/write-review\/(\d+)$/, '/v2/store/$1/write-review/$2'],
    [/^\/store\/[a-z0-9-]+\/signup(\/.*)?$/, null], // no v2 signup yet
    [/^\/store\/([a-z0-9-]+)(\/.*)?$/, '/v2/store/$1$2'], // about / collection / catch-all

    // Browse
    [/^\/marketplace(\/.*)?$/, '/v2/marketplace$1'],
    [/^\/(?:search|wall)(\/.*)?$/, '/v2/listings$1'],
    [/^\/listings(\/.*)?$/, '/v2/listings$1'],
    [/^\/page\/([a-z0-9-]+)$/, '/v2/page/$1'],
    [/^\/retailers$/, '/v2/retailers'],
    [/^\/retailers\/([a-z0-9-]+)$/, '/v2/retailers/$1'],
    [/^\/brands$/, '/v2/brands'],
    [/^\/brands\/([a-z0-9-]+)$/, '/v2/brands/$1'],
    [/^\/invite-retailers$/, '/v2/invite-retailers'],

    // Partner acquisition landing pages (TICA + buyer associations). The
    // association pages accept both the canonical /:slug URLs and the legacy
    // /retailer-:slug names, both landing on the same /v2/:slug page.
    [/^\/tica$/, '/v2/tica'],
    [/^\/retailer-(prisma|soennecken)$/, '/v2/$1'],
    [/^\/(prisma|soennecken)$/, '/v2/$1'],

    // Cart & checkout
    [/^\/cart$/, '/v2/cart'],
    [/^\/checkout(\/.*)?$/, '/v2/checkout'],
    [/^\/order\/([a-zA-Z0-9-]+)\/confirmation$/, '/v2/order/$1/confirmation'],
    // Token-based payment for brand-created direct orders and the invoice short-pay
    // link, migrated to the v2 OrderPayment page (routes marketplace.order-payment
    // and marketplace.pay). The old /order/:token/payment page is the same flow.
    [/^\/order\/direct\/([a-zA-Z0-9-]+)$/, '/v2/order/direct/$1'],
    [/^\/order\/([a-zA-Z0-9-]+)\/payment$/, '/v2/order/direct/$1'],
    [/^\/pay\/([a-zA-Z0-9-]+)$/, '/v2/pay/$1'],
    // Payment-provider return callback (migrated to the v2 OrderReturn page): it syncs
    // the returning payment and forwards to confirmation or back to the payment page.
    [/^\/order\/([a-zA-Z0-9-]+)\/return\/([A-Za-z0-9_-]+)$/, '/v2/order/$1/return/$2'],

    // Plus
    [/^\/plus$/, '/v2/plus'],

    // Account
    [/^\/account\/orders$/, '/v2/account/orders'],
    [/^\/account\/orders\/(\d+)$/, '/v2/account/orders/$1'],
    [/^\/account\/orders\/(\d+)\/updates$/, '/v2/account/orders/$1/updates'],
    [/^\/account\/invoices$/, '/v2/account/invoices'],
    [/^\/account\/favorites$/, '/v2/account/favorites'],
    [/^\/account\/brands$/, '/v2/account/brands'],
    [/^\/account\/inbox(\/\d+)?$/, '/v2/account/inbox$1'],
    [/^\/account\/edit$/, '/v2/account/edit'],
    [/^\/account\/plus$/, '/v2/account/plus'],
    [/^\/account\/plus\/subscribe$/, '/v2/account/plus/subscribe'],
    [/^\/account\/plus\/subscribe\/return$/, '/v2/account/plus/subscribe/return'],
    [/^\/email-settings\/([^/]+)$/, '/v2/email-settings/$1'],

    // Dropshipping: the v2 dropshipping environment is not launched yet, so every
    // /ds path passes through and stays on the v1 (Nuxt) dropshipping experience.
    // (No rules here on purpose; do not add /ds -> /v2/ds mappings until v2 ds ships.)
]

/**
 * Split a leading locale prefix off a pathname.
 *
 * @param {string} pathname e.g. "/nl/store/foo" or "/store/foo"
 * @returns {{ locale: string, path: string }} locale is "en" when no prefix is present
 */
export function splitLocale(pathname) {
    const match = pathname.match(LOCALE_PREFIX_RE)

    if (match) {
        return {
            locale: match[1].toLowerCase(),
            path: pathname.slice(match[0].length) || '/',
        }
    }

    return { locale: 'en', path: pathname || '/' }
}

/**
 * Re-apply a locale prefix to a path. English ("en") stays prefix-less.
 *
 * @param {string} locale
 * @param {string} path
 * @returns {string}
 */
export function applyLocale(locale, path) {
    if (locale && locale !== 'en') {
        return '/' + locale + (path === '/' ? '/' : path)
    }

    return path
}

/**
 * Map a locale-stripped, query-stripped pathname to its v2 equivalent.
 *
 * @param {string} path
 * @returns {string|null} the v2 path, or null when the path should not be redirected
 */
export function mapPathToV2(path) {
    const normalized = path !== '/' ? path.replace(/\/+$/, '') : path

    for (const [matcher, target] of RULES) {
        if (matcher.test(normalized)) {
            return target === null ? null : normalized.replace(matcher, target)
        }
    }

    return null
}

/**
 * Resolve the localized v2 path for a (possibly locale-prefixed) pathname.
 * Returns null when there is nothing to redirect (already on v2, unknown path,
 * or an explicit pass-through).
 *
 * @param {string} pathname pathname only, no query string
 * @returns {string|null}
 */
export function localizedV2Path(pathname) {
    const { locale, path } = splitLocale(pathname)
    const mapped = mapPathToV2(path)

    if (!mapped) {
        return null
    }

    return applyLocale(locale, mapped)
}
